if (!customElements.get("cart-notification")) {
  class CartNotification extends HTMLElement {
    constructor() {
      super();
      this.notification = document.getElementById("cart-notification");
      this.header = document.querySelector("sticky-header");
      this.onBodyClick = this.handleBodyClick.bind(this);
      this.closeTimeout = null;
      this.latestItems = [];
      this.latestAddedItem = null;
      this.latestTotalCount = 0;

      if (!this.notification) return;

      this.notification.addEventListener(
        "keyup",
        (evt) => evt.code === "Escape" && this.close()
      );

      if (typeof window.subscribe === "function") {
        window.subscribe("cart-update", (data) => {
          if (window.location.pathname.includes("/cart")) return;

          if (data && data.cartData) {
            const cartWithItems = data.cartData;

            this.fetchCartSections(cartWithItems)
              .then((cartWithSections) => {
                const notificationData = {
                  key:
                    data.key ||
                    data.variantId ||
                    (cartWithSections.items && cartWithSections.items[0]?.key),
                  items: cartWithSections.items || [],
                };

                this.renderContents(notificationData);
              })
              .catch((error) => {
                console.error("🔔 [CART] Error fetching cart sections:", error);
              });
          }
        });
      }
    }

    fetchCartSections(cart) {
      if (
        window.location.pathname.includes("/products/") ||
        window.location.pathname.includes("/collections/")
      ) {
        return Promise.resolve(cart);
      }

      return Promise.resolve(cart);
    }

    open() {
      this.classList.add("active");
      this.notification.classList.add("animate", "active");
      document.documentElement.classList.add("mini-cart-open");
      document.body.classList.add("mini-cart-open");

      this.notification.addEventListener(
        "transitionend",
        () => {
          this.notification.focus();
          if (typeof trapFocus === "function") {
            trapFocus(this.notification);
          }
        },
        { once: true }
      );

      document.body.addEventListener("click", this.onBodyClick);
    }

    close() {
      this.classList.remove("active");
      this.notification.classList.remove("active");
      document.documentElement.classList.remove("mini-cart-open");
      document.body.classList.remove("mini-cart-open");

      document.body.removeEventListener("click", this.onBodyClick);
      if (typeof removeTrapFocus === "function") {
        removeTrapFocus(this.activeElement);
      }
    }

    handleBodyClick(evt) {
      const target = evt.target;

      if (target.classList?.contains("ml-mini-cart-overlay")) {
        this.close();
        return;
      }

      if (
        target !== this.notification &&
        !target.closest("cart-notification") &&
        !target.closest(".ml-mini-cart")
      ) {
        const disclosure = target.closest("details-disclosure, header-menu");
        this.activeElement = disclosure
          ? disclosure.querySelector("summary")
          : null;
        this.close();
      }
    }

    renderContents(parsedState) {
      if (!this.notification) return;

      this.cartItemKey = parsedState.key;

      if (
        !parsedState.items ||
        !Array.isArray(parsedState.items) ||
        parsedState.items.length === 0
      ) {
        fetch("/cart.js")
          .then((response) => response.json())
          .then((cart) => {
            if (cart.items && cart.items.length) {
              this.finishRender(cart.items);
            } else {
              this.showBasicNotification();
            }
          })
          .catch((error) => {
            console.error("🔔 [CART] Error fetching cart data:", error);
            this.showBasicNotification();
          });
        return;
      }

      this.finishRender(parsedState.items);
    }

    finishRender(items) {
      this.latestItems = items || [];
      this.latestAddedItem = this.findLatestAddedItem(this.latestItems);
      this.latestTotalCount = this.latestItems.reduce(
        (total, item) => total + item.quantity,
        0
      );

      this.updateCartIconBubble(this.latestItems);

      this.renderMobileSheet();

      if (this.header) this.header.reveal();
      this.open();
    }

    findLatestAddedItem(items) {
      if (!items || !items.length) return null;

      if (this.cartItemKey) {
        const foundItem = items.find(
          (item) =>
            item.key === this.cartItemKey ||
            item.variant_id === parseInt(this.cartItemKey)
        );
        if (foundItem) return foundItem;
      }

      return items[0];
    }

    getItemImage(item) {
      let productImage = "";

      if (item.featured_image && item.featured_image.url) {
        productImage = item.featured_image.url;
      } else if (item.image) {
        productImage =
          typeof item.image === "string" ? item.image : item.image.src || "";
      } else if (
        item.product_has_only_default_variant &&
        item.product_featured_image
      ) {
        productImage = item.product_featured_image;
      } else if (item.featured_media && item.featured_media.preview_image) {
        productImage = item.featured_media.preview_image.src;
      }

      if (!productImage && item.url) {
        const productUrlParts = item.url.split("?")[0].split("/");
        const productHandle = productUrlParts[productUrlParts.length - 1];
        productImage = `/products/${productHandle}/featured_image`;
      }

      return productImage;
    }

    getMobileProductHTML(item, isLatest = false) {
      const productImage = this.getItemImage(item);
      const productTitle = item.product_title || item.title || "Produkt";
      const productQuantity = item.quantity || 1;
      const linePrice = this.formatMoney(item.final_line_price);
      const variantTitle =
        item.variant_title && item.variant_title !== "Default Title"
          ? `<div class="ml-mini-cart-product__variant">${item.variant_title}</div>`
          : "";

      return `
        <div class="ml-mini-cart-product ${isLatest ? "ml-mini-cart-product--latest" : ""}">
          ${
            productImage
              ? `<div class="ml-mini-cart-product__image">
                  <img src="${productImage}" alt="${productTitle}" width="88" height="88" loading="lazy">
                </div>`
              : ""
          }
          <div class="ml-mini-cart-product__info">
            ${
              isLatest
                ? `<p class="ml-mini-cart-product__eyebrow">Tilføjet til kurven</p>`
                : `<p class="ml-mini-cart-product__eyebrow">Allerede i kurven</p>`
            }
            <h3 class="ml-mini-cart-product__title">${productTitle}</h3>
            ${variantTitle}
            <div class="ml-mini-cart-product__price">
              <span>${productQuantity}×</span>
              <span>${linePrice}</span>
            </div>
          </div>
        </div>
      `;
    }

    renderMobileSheet() {
      const subtotal = this.formatMoney(this.getSubtotal());
      const totalCount = this.latestTotalCount;
      const progressHTML = this.getShippingProgressHTML();

      const latestItemHtml = this.latestAddedItem
        ? this.getMobileProductHTML(this.latestAddedItem, true)
        : "";

      const otherItems = this.latestItems.filter(
        (item) => !this.latestAddedItem || item.key !== this.latestAddedItem.key
      );

      const otherItemsHtml =
        otherItems.length > 0
          ? `
            <div class="ml-mini-cart__other-items">
              <div class="ml-mini-cart__other-items-title">Øvrige varer i kurven</div>
              <div class="ml-mini-cart__other-items-list">
                ${otherItems.map((item) => this.getMobileProductHTML(item, false)).join("")}
              </div>
            </div>
          `
          : "";

      this.notification.innerHTML = `
        <div class="ml-mini-cart-overlay"></div>
        <div class="ml-mini-cart" role="dialog" aria-modal="true" aria-label="Din kurv">
          <div class="ml-mini-cart__handle"></div>

          <div class="ml-mini-cart__header">
            <div class="ml-mini-cart__header-left">
              <h2>Din kurv</h2>
            </div>
            <div class="ml-mini-cart__header-right">
              <div class="ml-mini-cart__header-subtotal">
                <span>Subtotal</span>
                <strong>${subtotal}</strong>
              </div>
              <button type="button" class="ml-mini-cart__close" aria-label="Luk">×</button>
            </div>
          </div>

          <div class="ml-mini-cart__body">
            <div class="ml-mini-cart__progress-wrap">
              ${progressHTML}
            </div>

            ${latestItemHtml}
            ${otherItemsHtml}
          </div>

          <div class="ml-mini-cart__footer">
            <button type="button" class="ml-mini-cart__continue">Shop videre</button>
            <a href="/cart" class="ml-mini-cart__view-cart">Se indkøbskurv (${totalCount})</a>
          </div>
        </div>
      `;

      const closeBtn = this.notification.querySelector(".ml-mini-cart__close");
      const continueBtn = this.notification.querySelector(".ml-mini-cart__continue");
      const overlay = this.notification.querySelector(".ml-mini-cart-overlay");

      if (closeBtn) closeBtn.addEventListener("click", this.close.bind(this));
      if (continueBtn) continueBtn.addEventListener("click", this.close.bind(this));
      if (overlay) overlay.addEventListener("click", this.close.bind(this));
    }

    getSubtotal() {
      return Array.isArray(this.latestItems)
        ? this.latestItems.reduce((total, item) => total + item.final_line_price, 0)
        : 0;
    }

    getShippingProgressHTML() {
      const cartTotal = Array.isArray(this.latestItems)
        ? this.latestItems.reduce((total, item) => {
            if (item.properties && item.properties._free_gift) return total;
            return total + item.final_line_price;
          }, 0)
        : 0;

      const shippingThreshold =
        window.theme && window.theme.freeShippingThreshold
          ? parseFloat(window.theme.freeShippingThreshold)
          : 599;

      const shippingThresholdInCents = shippingThreshold * 100;
      const shippingThresholdMet = cartTotal >= shippingThresholdInCents;

      const shippingText = shippingThresholdMet
        ? "Fri fragt opnået"
        : `Køb for ${shippingThreshold} kr for Fri Fragt`;

      const progressPercentage =
        shippingThresholdInCents > 0
          ? Math.min((cartTotal / shippingThresholdInCents) * 100, 100)
          : 0;

      return `
        <div class="free-gift-progress-bar">
          <div class="free-gift-marker free-gift-marker-initial active" style="left: 0;"></div>
          <div class="free-gift-progress-fill" style="width: ${progressPercentage}%;"></div>
          <div class="free-gift-marker free-gift-marker-shipping ${shippingThresholdMet ? "active" : ""}" style="left: 100%;"></div>
        </div>
        <div class="free-gift-labels free-gift-labels--single">
          <span class="free-gift-labels__single">${shippingText}</span>
        </div>
      `;
    }

    updateCartIconBubble(items) {
      if (!items) return 0;

      const cartIconBubble = document.getElementById("cart-icon-bubble");
      if (!cartIconBubble) return 0;

      const totalQuantity = items.reduce(
        (total, item) => total + item.quantity,
        0
      );

      const cartCountBubble = cartIconBubble.querySelector(".cart-count-bubble");

      if (totalQuantity === 0) {
        if (cartCountBubble) cartCountBubble.remove();
      } else {
        if (!cartCountBubble) {
          const newBubble = document.createElement("div");
          newBubble.classList.add("cart-count-bubble");
          cartIconBubble.appendChild(newBubble);
        }

        const countBubble = cartIconBubble.querySelector(".cart-count-bubble");
        if (countBubble) {
          countBubble.innerHTML = `
            ${totalQuantity < 100 ? `<span aria-hidden="true">${totalQuantity}</span>` : ""}
            <span class="visually-hidden">Cart count: ${totalQuantity}</span>
          `;
        }
      }

      return totalQuantity;
    }

    formatMoney(cents) {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }

      const value = cents / 100;
      return value.toFixed(2) + " kr";
    }

    getSectionsToRender() {
      return [];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    showBasicNotification() {
      this.latestItems = [];
      this.latestAddedItem = null;
      this.latestTotalCount = 0;
      this.renderMobileSheet();
      if (this.header) this.header.reveal();
      this.open();
    }
  }

  customElements.define("cart-notification", CartNotification);
}